import { DENOMINATIONS } from '../constants/operations.js';
import { PAYMENT_MODES } from '../constants/pos.js';

/**
 * Shift and day (Z) report models. The server fills shift counters as bills, voids, refunds and
 * cash entries happen; these functions turn them into what the screen, the thermal printer and
 * the email show — the same numbers everywhere.
 */
const modeLabel = (k) => PAYMENT_MODES.find((m) => m.key === k)?.label ?? k;

export const emptyShiftTotals = () => ({
  billCount: 0,
  salesPaise: 0,
  discountPaise: 0,
  taxPaise: 0,
  payments: {},
  voidCount: 0,
  voidPaise: 0,
  refundCount: 0,
  refundPaise: 0,
});

export const emptyShiftCash = () => ({
  salesPaise: 0,
  refundsPaise: 0,
  voidsPaise: 0,
  inPaise: 0,
  outPaise: 0,
  duesPaise: 0,
});

/** Cash that should be in the drawer: float + cash sales + dues collected − refunds/voids + cash in − cash out. */
export function expectedCash(shift) {
  const c = { ...emptyShiftCash(), ...(shift.cash ?? {}) };
  return (
    (shift.openingFloatPaise ?? 0) + c.salesPaise + c.duesPaise - c.refundsPaise - c.voidsPaise + c.inPaise - c.outPaise
  );
}

/** Counted cash from the denomination grid (counts per note/coin) plus loose change in paise. */
export function countedTotal(denominations = {}, otherPaise = 0) {
  return (
    Object.entries(denominations).reduce((s, [d, n]) => s + Number(d) * 100 * (Number(n) || 0), 0) + (otherPaise || 0)
  );
}

export function denominationRows(denominations = {}) {
  return DENOMINATIONS.filter((d) => Number(denominations[d]) > 0).map((d) => ({
    value: d,
    count: Number(denominations[d]),
    amountPaise: d * 100 * Number(denominations[d]),
  }));
}

/**
 * kind: 'X' (mid-shift snapshot) | 'SHIFT' (closed shift).
 * shift: { shiftNo, tillLabel, openedAt, openedBy, closedAt, closedBy, cashiers[], openingFloatPaise,
 *          totals, cash, counted: { denominations, otherPaise, totalPaise }, status }
 */
export function buildShiftReport(
  shift,
  { kind = 'X', entries = [], businessName, outletName, timezone, now = new Date() },
) {
  const t = { ...emptyShiftTotals(), ...(shift.totals ?? {}) };
  const c = { ...emptyShiftCash(), ...(shift.cash ?? {}) };
  const expected = expectedCash(shift);
  const closed = kind === 'SHIFT' && shift.counted;
  const outByCategory = {};
  for (const e of entries.filter((x) => x.type === 'out'))
    outByCategory[e.category ?? 'Other'] = (outByCategory[e.category ?? 'Other'] ?? 0) + e.amountPaise;
  return {
    kind,
    title: kind === 'X' ? 'X REPORT (MID-SHIFT)' : 'SHIFT CLOSE REPORT',
    businessName,
    outletName,
    timezone: timezone ?? 'Asia/Kolkata',
    shiftNo: shift.shiftNo,
    tillLabel: shift.tillLabel,
    openedAt: shift.openedAt,
    openedBy: shift.openedBy?.name ?? null,
    closedAt: shift.closedAt ?? null,
    closedBy: shift.closedBy?.name ?? null,
    cashiers: (shift.cashiers ?? []).map((x) => x.name),
    printedAt: now,
    sales: {
      billCount: t.billCount,
      salesPaise: t.salesPaise,
      discountPaise: t.discountPaise,
      taxPaise: t.taxPaise,
      voidCount: t.voidCount,
      voidPaise: t.voidPaise,
      refundCount: t.refundCount,
      refundPaise: t.refundPaise,
      netPaise: t.salesPaise - t.voidPaise - t.refundPaise,
    },
    payments: Object.entries(t.payments ?? {})
      .filter(([, v]) => v)
      .map(([mode, amountPaise]) => ({ mode, label: modeLabel(mode), amountPaise })),
    cash: {
      openingFloatPaise: shift.openingFloatPaise ?? 0,
      salesPaise: c.salesPaise,
      refundsPaise: c.refundsPaise,
      voidsPaise: c.voidsPaise,
      inPaise: c.inPaise,
      outPaise: c.outPaise,
      duesPaise: c.duesPaise,
      expectedPaise: expected,
      countedPaise: closed ? shift.counted.totalPaise : null,
      variancePaise: closed ? shift.counted.totalPaise - expected : null,
    },
    cashOut: Object.entries(outByCategory).map(([category, amountPaise]) => ({ category, amountPaise })),
    denominations: closed ? denominationRows(shift.counted.denominations) : [],
    otherPaise: closed ? (shift.counted.otherPaise ?? 0) : 0,
  };
}
